import { AnyAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AuthUser } from "@ifgengineering/client-auth-sdk";
import AuthActionTypes from "@state/auth/auth.types";
import { getViewAsSessionId } from "@helpers/viewAs/viewAsSession";
import {
  clearViewAsSession,
  readStoredViewAsSessionId,
} from "@helpers/viewAs/viewAsStorage";

export interface ViewAsState {
  active: boolean;
  sessionId: string | null;
  targetName: string | null;
  staffEmail: string | null;
  expiresAt: string | null;
  /** True from the moment Exit is clicked until the server has answered; no request leaves meanwhile. */
  ending: boolean;
  ended: boolean;
  /** False when Exit could not confirm the end with the server. */
  endConfirmed: boolean;
}

export const INITIAL_STATE: ViewAsState = {
  active: false,
  sessionId: null,
  targetName: null,
  staffEmail: null,
  expiresAt: null,
  ending: false,
  ended: false,
  endConfirmed: true,
};

interface ViewAsSessionEndedPayload {
  endConfirmed?: boolean;
}

type UserInfoAction = PayloadAction<AuthUser | undefined>;

// Keyed on action types: auth.actions sits in an import cycle with the store that the reducer must not join.
const isUserInfoAction = (action: AnyAction): action is UserInfoAction =>
  action.type === `${AuthActionTypes.GET_AUTH_ACCOUNT_DATA}/fulfilled` ||
  action.type === AuthActionTypes.UPDATE_USER;

const hydrate = (state: ViewAsState, action: UserInfoAction): void => {
  if (state.ended) return;

  const viewAs = action.payload?.viewAs;
  const boundSessionId = getViewAsSessionId();

  // Account data that does not name this page's session means the server did not honour the header: the tab ends.
  if (boundSessionId && viewAs?.sessionId !== boundSessionId) {
    if (readStoredViewAsSessionId() === boundSessionId) clearViewAsSession();
    state.active = false;
    state.ended = true;
    return;
  }

  state.active = !!viewAs;
  state.sessionId = viewAs?.sessionId ?? null;
  state.targetName = viewAs?.targetName ?? null;
  state.staffEmail = viewAs?.staffEmail ?? null;
  state.expiresAt = viewAs?.expiresAt ?? null;
};

const viewAsSlice = createSlice({
  name: "viewAs",
  initialState: INITIAL_STATE,
  reducers: {
    viewAsSessionEnding: (state) => {
      state.ending = true;
    },
    viewAsSessionEnded: (
      state,
      { payload }: PayloadAction<ViewAsSessionEndedPayload | undefined>
    ) => {
      state.active = false;
      state.ending = false;
      state.ended = true;
      state.endConfirmed = payload?.endConfirmed ?? true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isUserInfoAction, hydrate);
  },
});

export const { viewAsSessionEnded, viewAsSessionEnding } = viewAsSlice.actions;

export default viewAsSlice.reducer;
